import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import { HeaderComponent } from '../header/header.component';
import { MatButton } from '@angular/material/button';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { BankAccountEditComponent } from './bank-account-edit/bank-account-edit.component';
import { UserOptionsComponent } from './client-options/client-options.component';
import { ApiIntegrationsComponent } from './api-integrations/api-integrations.component';
import { NotificationSettingsComponent } from './notification-settings/notification-settings.component';
import { TranslateModule } from '@ngx-translate/core';

const CLIENT_PROFILE_TABS = [
  'bank-account-edit',
  'notification-settings',
  'user-options',
  'api-integrations',
] as const;
export type  ClientProfileTab = typeof CLIENT_PROFILE_TABS[number];

const DEFAULT_CLIENT_PROFILE_TAB: ClientProfileTab = 'notification-settings';

function isClientProfileTab(value: string): value is ClientProfileTab {
  return (CLIENT_PROFILE_TABS as readonly string[]).includes(value);
}

@Component({
  selector: 'app-client-profile',
  templateUrl: './client-profile.component.html',
  styleUrls: ['./client-profile.component.scss'],
  imports: [
    HeaderComponent,
    MatButton,
    PayeeIconComponent,
    BankAccountEditComponent,
    UserOptionsComponent,
    ApiIntegrationsComponent,
    NotificationSettingsComponent,
    TranslateModule,
  ],
})
export class ClientProfileComponent implements OnInit, OnDestroy {
  private clientsService = inject(ClientsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private readonly destroy = new Subject<void>();

  tab: ClientProfileTab = DEFAULT_CLIENT_PROFILE_TAB;

  get client(): SelectedClient | null {
    return this.clientsService.selectedClientSubject.value;
  }

  ngOnInit(): void {
    void this.syncSidebarWithSelectedClient();

    this.clientsService.clientsSubject
      .pipe(
        filter((clients): clients is SelectedClient[] => !!clients?.length),
        take(1),
        takeUntil(this.destroy),
      )
      .subscribe(() => {
        void this.syncSidebarWithSelectedClient();
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy))
      .subscribe((params) => {
        const tabParam = params.get('tab');
        if (tabParam && isClientProfileTab(tabParam)) {
          this.tab = tabParam;
          return;
        }

        if (tabParam) {
          void this.setTabQueryParam(DEFAULT_CLIENT_PROFILE_TAB, true);
          return;
        }

        this.tab = DEFAULT_CLIENT_PROFILE_TAB;
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  selectTab(tab: ClientProfileTab): void {
    void this.setTabQueryParam(tab);
  }

  private async syncSidebarWithSelectedClient(): Promise<void> {
    const clientId = this.route.snapshot.queryParamMap.get('client_id');
    try {
      const client = clientId
        ? await this.clientsService.getClient(clientId)
        : await this.clientsService.getSelectedClient();
      this.clientsService.selectClientWithCaseFilter(client);
    } catch {
      // Clients not loaded yet or unknown client_id.
    }
  }

  private setTabQueryParam(
    tab: ClientProfileTab,
    replaceUrl = false,
  ): Promise<boolean> {
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  async copyIdToClipboard(event: Event): Promise<void> {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(
        this.client?.client_reference_id ?? '',
      );
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
    }
  }
}
